import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { useCallback, useMemo } from "react";
import { useTenant } from "hooks/settings";
import {
  CheckParameterFeasibilityRequest,
  ParameterFeasibilityReport,
  ParameterConflict,
  useCheckParameterFeasibilityMutation,
  useGetMaterialChemistrySetQuery,
  useGetMaterialPhysicsSetQuery,
  useGetFixedMixDefaultsQuery,
} from "src/store/api/generatedApi";
import { prepareFeasibilityCheck } from "./parameterFeasibilityRequest";

import {
  PendingRequest,
  RequestCompletion,
  useDebouncedRequest,
} from "./useDebouncedRequest";

const DEBOUNCE_MS = 400;
const EMPTY_CONFLICTS: ParameterConflict[] = [];

interface FeasibilityInput {
  request: CheckParameterFeasibilityRequest;
  tenantName: string;
  chemistrySetId: number | null;
  physicsSetId: number | null;
  fixedMixSetId: number | null;
}

type CheckFeasibility = (
  request: CheckParameterFeasibilityRequest
) => PendingRequest<ParameterFeasibilityReport>;

export const useFeasibilityCheck = ({
  request,
  tenantName,
  check,
  chemistrySetId = null,
  physicsSetId = null,
  fixedMixSetId = null,
}: {
  request: CheckParameterFeasibilityRequest | null;
  tenantName: string;
  check: CheckFeasibility;
  chemistrySetId?: number | null;
  physicsSetId?: number | null;
  fixedMixSetId?: number | null;
}) => {
  const input = useMemo(
    () =>
      request === null
        ? null
        : { request, tenantName, chemistrySetId, physicsSetId, fixedMixSetId },
    [request, tenantName, chemistrySetId, physicsSetId, fixedMixSetId]
  );
  const checkChanges = useCallback(
    (
      input: FeasibilityInput,
      previous: RequestCompletion<
        FeasibilityInput,
        ParameterFeasibilityReport
      > | null
    ) => {
      const { request: changedRequest, retainedConflicts } =
        prepareFeasibilityCheck({
          request: input.request,
          completed:
            previous?.status === "success" &&
            previous.request.tenantName === input.tenantName &&
            previous.request.chemistrySetId === input.chemistrySetId &&
            previous.request.physicsSetId === input.physicsSetId &&
            previous.request.fixedMixSetId === input.fixedMixSetId
              ? {
                  request: previous.request.request,
                  conflicts: previous.response.conflicts,
                }
              : null,
        });
      if (changedRequest === null)
        return {
          response: Promise.resolve({ conflicts: retainedConflicts }),
          abort: null,
        };
      const pending = check(changedRequest);
      return {
        response: pending.response.then(({ conflicts }) => ({
          conflicts: [...retainedConflicts, ...conflicts],
        })),
        abort: pending.abort,
      };
    },
    [check]
  );
  const { completion, isChecking, hasError } = useDebouncedRequest({
    request: input,
    check: checkChanges,
    delay: DEBOUNCE_MS,
  });
  return {
    conflicts:
      request === null ||
      completion?.request.tenantName !== tenantName ||
      completion.status !== "success"
        ? EMPTY_CONFLICTS
        : completion.response.conflicts,
    isChecking,
    hasError,
  };
};

export const useParameterFeasibility = (
  request: CheckParameterFeasibilityRequest | null
) => {
  const tenantName = useTenant();
  const [check] = useCheckParameterFeasibilityMutation();
  const chemistry = useGetMaterialChemistrySetQuery({
    tenantName,
    materialChemistrySetId: "default",
  });
  const physics = useGetMaterialPhysicsSetQuery({
    tenantName,
    materialPhysicsSetId: "default",
  });
  const fixedMixDefaultsEnabled = useIsBehindFeatureFlag("fixed_mix_defaults");
  const fixedMixDefaults = useGetFixedMixDefaultsQuery(
    { tenantName },
    { skip: !fixedMixDefaultsEnabled }
  );
  const checkRequest = useCallback(
    (candidate: CheckParameterFeasibilityRequest) => {
      const pending = check({
        tenantName,
        checkParameterFeasibilityRequest: candidate,
      });
      return {
        response: pending.unwrap(),
        abort: () => {
          pending.abort();
        },
      };
    },
    [check, tenantName]
  );
  return useFeasibilityCheck({
    request: chemistry.isLoading || physics.isLoading ? null : request,
    tenantName,
    check: checkRequest,
    chemistrySetId: chemistry.currentData?.id ?? null,
    physicsSetId: physics.currentData?.id ?? null,
    fixedMixSetId: fixedMixDefaults.currentData?.effective.id ?? null,
  });
};
